import { useInfiniteQuery } from '@tanstack/react-query';

import { API } from 'core/API';
import { INITIAL_PAGINATION } from 'core/constants';

import {
	AdminSearchRequest,
	ADMIN_SEARCH_FILTER_TYPE,
	ISearchOpportunitiesSort,
	PaginationRequest,
	SearchOpportunityResponse,
} from '@joc/api-gateway/lib/api-client';

import { buildAdminSearchRequest, SuperAdminOpportunitiesFilters, SuperAdminOpportunitiesState } from '../mappers';

export const SUPER_ADMIN_OPPORTUNITIES_QUERY_KEY = 'superAdminOpportunities';

const getQueryStateKey = ({ search, sort, filters }: SuperAdminOpportunitiesState) => {
	const from = filters?.startDay?.fromDate ? filters.startDay.fromDate.toISOString() : '';
	const to = filters?.startDay?.toDate ? filters.startDay.toDate.toISOString() : '';
	const organisationIds = filters?.organisationIds?.length ? filters.organisationIds.join(',') : '';

	return {
		search: search || '',
		sortBy: sort?.sortBy || '',
		sortDir: sort?.sortDir || '',
		fromDate: from,
		toDate: to,
		organisationIds,
	};
};

export const useSuperAdminOpportunities = ({
	opportunityFilterType,
	search,
	sort,
	filters,
	pagination,
}: {
	opportunityFilterType: ADMIN_SEARCH_FILTER_TYPE;
	search?: string;
	sort?: ISearchOpportunitiesSort;
	filters?: SuperAdminOpportunitiesFilters;
	pagination?: { take?: number };
}) =>
	useInfiniteQuery<SearchOpportunityResponse, unknown>({
		queryKey: [
			SUPER_ADMIN_OPPORTUNITIES_QUERY_KEY,
			opportunityFilterType,
			getQueryStateKey({ search, sort, filters } as SuperAdminOpportunitiesState),
			pagination?.take ?? INITIAL_PAGINATION.take,
		],
		initialPageParam: 0,
		retry: false,
		refetchOnWindowFocus: false,
		queryFn: async ({ pageParam = 0 }) => {
			const take = pagination?.take ?? INITIAL_PAGINATION.take;

			const body: AdminSearchRequest = buildAdminSearchRequest({
				opportunityFilterType,
				state: {
					search,
					sort,
					filters,
				},
				pagination: PaginationRequest.fromJS({
					skip: pageParam as number,
					take,
				}),
			});

			return API.searchOpportunitiesByAdmin(undefined, body);
		},
		getNextPageParam: (lastPage, allPages) => {
			const take = pagination?.take ?? INITIAL_PAGINATION.take;
			return lastPage.records.length < take ? undefined : allPages.length * take;
		},
	});
